import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { applyActionCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import FormLayout from '../components/FormLayout';
import FormHeader from '../components/FormHeader';
import Form from '../components/Form';
import Input from '../components/Input';
import Button from '../components/Button';
import Alert from '../components/Alert';
import prairiLogo from '../assets/prairiwhite.png';

// ── Toast ─────────────────────────────────────────────────────────────────────

interface ToastProps {
  visible: boolean;
}

const VerifiedToast: React.FC<ToastProps> = ({ visible }) => {
  if (!visible) return null;
  return createPortal(
    <div
      className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2.5 bg-emerald-600 text-white px-5 py-3 rounded-xl shadow-2xl text-sm font-medium">
        <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        Your email has been verified
      </div>
    </div>,
    document.body
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

type Status = 'processing' | 'success' | 'error';

function passwordProblem(password: string, confirm: string): string {
  if (password.length < 8) return 'Password must be at least 8 characters long';
  if (!/\d/.test(password)) return 'Password must contain at least one number';
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    return 'Password must contain at least one special character';
  }
  if (password !== confirm) return 'Passwords do not match';
  return '';
}

function RequirementRow({ met, label }: { met: boolean; label: string }) {
  return (
    <div className="flex items-center">
      {met ? (
        <svg className="w-4 h-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        <div className="w-2 h-2 mr-2 bg-gray-400 rounded-full" />
      )}
      <span className="text-gray-400">{label}</span>
    </div>
  );
}

/** Same card shell as Create Your Password (`FinalStepPage`). */
const ResetPasswordPanel: React.FC<{ oobCode: string | null }> = ({ oobCode }) => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errorMsg, setErrorMsg] = useState(
    oobCode ? '' : 'This link is invalid or has already been used.'
  );
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!oobCode) return;
    const problem = passwordProblem(password, confirm);
    if (problem) {
      setErrorMsg(problem);
      return;
    }
    setSubmitting(true);
    setErrorMsg('');
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch (err) {
      const code = err && typeof err === 'object' && 'code' in err ? String(err.code) : '';
      if (code === 'auth/expired-action-code' || code === 'auth/invalid-action-code') {
        setErrorMsg('This link is invalid or has already been used.');
      } else {
        setErrorMsg('Could not reset your password. Please request a new link.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormLayout>
      <FormHeader
        title={done ? 'Password updated' : 'Reset your password'}
        subtitle={done ? 'Taking you to login…' : 'Choose a new password for your PRAIRI account'}
      />

      {done ? null : (
        <Form onSubmit={handleSubmit}>
          <Input
            type="password"
            value={password}
            onChange={e => {
              setPassword(e.target.value);
              if (errorMsg) setErrorMsg('');
            }}
            required
            placeholder="Enter your password"
            label="Password"
            showPasswordToggle
            showPassword={showPassword}
            onTogglePassword={() => setShowPassword(current => !current)}
            disabled={submitting || !oobCode}
          />

          <Input
            type="password"
            value={confirm}
            onChange={e => {
              setConfirm(e.target.value);
              if (errorMsg) setErrorMsg('');
            }}
            required
            placeholder="Confirm your password"
            label="Confirm password"
            showPasswordToggle
            showPassword={showConfirm}
            onTogglePassword={() => setShowConfirm(current => !current)}
            disabled={submitting || !oobCode}
          />

          <div className="text-xs text-gray-300 text-left">
            <p className="mb-2">Password requirements:</p>
            <div className="space-y-1">
              <RequirementRow met={password.length >= 8} label="Minimum 8 characters" />
              <RequirementRow met={/\d/.test(password)} label="At least one number" />
              <RequirementRow
                met={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)}
                label="At least one special character"
              />
            </div>
          </div>

          {errorMsg ? <Alert type="error">{errorMsg}</Alert> : null}

          <Button type="submit" disabled={submitting || !oobCode}>
            {submitting ? (
              <div className="flex items-center justify-center">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Saving…
              </div>
            ) : (
              'Create Password'
            )}
          </Button>
        </Form>
      )}
    </FormLayout>
  );
};

const EmailActionPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState<Status>('processing');
  const [errorMsg, setErrorMsg] = useState('');
  const [showToast, setShowToast] = useState(false);
  const cancelled = useRef(false);

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  useEffect(() => {
    if (mode === 'resetPassword') return;
    cancelled.current = false;
    if (mode !== 'verifyEmail' || !oobCode) {
      setStatus('error');
      setErrorMsg('This link is invalid or has already been used.');
      return;
    }

    (async () => {
      try {
        await applyActionCode(auth, oobCode);

        // Reload Firebase user so emailVerified flips to true immediately
        if (auth.currentUser) {
          await auth.currentUser.reload();
          await refreshUser?.();
        }

        if (cancelled.current) return;
        setStatus('success');
        setShowToast(true);

        // Hide toast after 5 s
        setTimeout(() => {
          if (!cancelled.current) setShowToast(false);
        }, 5000);

        // Redirect after 1.5 s — read userType at redirect time (not from stale closure)
        setTimeout(() => {
          if (cancelled.current) return;
          const uid = auth.currentUser?.uid;
          const cachedType = uid ? localStorage.getItem(`prairi_ut_${uid}`) : null;
          const t = cachedType || user?.userType;
          if (t === 'investor') {
            navigate('/express-signup', { replace: true });
          } else if (t === 'startup') {
            navigate('/profile/startup', { replace: true });
          } else {
            navigate('/login', { replace: true, state: { emailVerified: true } });
          }
        }, 1500);
      } catch (err) {
        if (cancelled.current) return;
        setStatus('error');
        setErrorMsg(
          err instanceof Error
            ? err.message
            : 'Could not verify your email. The link may have expired.'
        );
      }
    })();

    return () => {
      cancelled.current = true;
    };
  }, [mode, oobCode]); // eslint-disable-line react-hooks/exhaustive-deps

  if (mode === 'resetPassword') {
    return <ResetPasswordPanel oobCode={oobCode} />;
  }

  // ── Loading ─────────────────────────────────────────────────────────────────

  if (status === 'processing') {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
          <p className="text-white text-lg">Verifying your email…</p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────────

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center space-y-6">
          <img src={prairiLogo} alt="PRAIRI" className="h-20 w-auto mx-auto object-contain" />
          <h1 className="text-2xl font-bold text-white">Verification failed</h1>
          <p className="text-gray-400 text-sm leading-relaxed">{errorMsg}</p>
          <button
            onClick={() => navigate('/verify-email', { replace: true })}
            className="bg-emerald-700 hover:bg-emerald-800 text-white font-medium py-2.5 px-6 rounded-lg transition-colors"
          >
            Resend verification email
          </button>
        </div>
      </div>
    );
  }

  // ── Success (brief loading screen shown while toast is live + redirect fires) ─

  return (
    <>
      <VerifiedToast visible={showToast} />
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-emerald-500" />
          <p className="text-white text-base">Taking you to your profile setup…</p>
        </div>
      </div>
    </>
  );
};

export default EmailActionPage;
